import { Button } from "@/components/button";
import { Section } from "@/components/section";
import { Select } from "@/components/select";
import { TextareaInput } from "@/components/textarea-input";
import { api } from "@/config/api-fetch";
import { DEFAULTS, POST_STATUSES, POST_TYPES, USERS } from "@/utils/constants";
import { ENUM, LIMITS } from "@/utils/constants";
import { css } from "@/utils/css";
import { utcDate, MS_IN_A_DAY, utcHour, utcMinute, utcWeekday } from "@/utils/date";
import { __ } from "@wordpress/i18n";
import { useState } from "react";
import { toast } from "react-toastify";
import { Tone } from "./tone";
import { FormBox } from "@/components/form-box";
import { getLanguageLabel, GPT_LANGUAGES } from "@/utils/language";
import { KnowledgeSources } from "./knowledge-sources";
import { Schedule } from "./schedule";
import { ArticleLength, lengthOptions } from "./article-length";
import { SlugMaxLength, slugMaxLengthOptions } from "./slug-max-length";
import { TONES } from "@/utils/tones";
import { useAutoPostings } from "@/hooks/use-auto-postings";
import { useProModal } from "@/components/pro-alert-box";

const users = USERS.map((user) => ({ value: user.id.toString(), label: user.name }));

export function CreateNewPosting() {
    const now = new Date();

    const [topics, setTopics] = useState("");
    const [tone, setTone] = useState(TONES[9]);
    const [modifiedTone, setModifiedTone] = useState<string>();
    const [language, setLanguage] = useState(DEFAULTS.LANGUAGE);
    const [slugLang, setSlugLang] = useState(DEFAULTS.LANGUAGE);
    const [length, setLength] = useState(lengthOptions[1]);
    const [slugMaxLength, setSlugMaxLength] = useState(slugMaxLengthOptions[1]);
    const [author, setAuthor] = useState(users[0]?.value);
    const [type, setType] = useState(POST_TYPES[0].id);
    const [status, setStatus] = useState(DEFAULTS.POST_STATUS);
    const [submitting, setSubmitting] = useState(false);
    const [files, setFiles] = useState<File[]>();
    const [expDate, setExpDate] = useState(new Date(now.getTime() + 7 * MS_IN_A_DAY));
    const [schedule, setSchedule] = useState({
        weekdays: [0, 1, 2, 3, 4, 5, 6],
        times: [{ id: "10:00 AM", hour: 10, minute: 0 }],
    });
    const { data: postings, isLoading: postingsLoading } = useAutoPostings();
    const { showProAlert } = useProModal();

    const handleSubmit = async () => {
        setSubmitting(true);

        try {
            const topicsList = topics
                .split("\n")
                .map((topic) =>
                    topic
                        .split(" ")
                        .filter((word) => !!word)
                        .join(" ")
                )
                .filter((topic) => !!topic);

            const { weekdays, times } = schedule;

            if (topicsList.length === 0) {
                throw new Error(__("At least one topic is required.", "antimanual"));
            }

            if (weekdays.length === 0) {
                throw new Error(__("At least one weekday is required.", "antimanual"));
            }

            if (times.length === 0) {
                throw new Error(__("At least one specific time is required.", "antimanual"));
            }

            const form = new FormData();
            if (files && files.length > 0) {
                files.forEach((file) => {
                    form.append("files[]", file);
                });
            }

            const payload = {
                topics: topicsList,
                tone: modifiedTone || tone.description,
                language: getLanguageLabel(language),
                min_length: length.min,
                max_length: length.max,
                slug_language: getLanguageLabel(slugLang),
                slug_max_length: slugMaxLength.max,
                author,
                type,
                status,
                weekdays: weekdays.map((day) => utcWeekday(day)),
                times: times.map((t) => ({ hour: utcHour(t.hour), minute: utcMinute(t.minute) })),
                exp_date: Math.floor(utcDate(expDate).getTime() / 1000),
            };

            for (const key in payload) {
                const value = payload[key as keyof typeof payload];

                if (typeof value === "object") {
                    form.append(key, JSON.stringify(value));
                    continue;
                }

                form.append(key, String(value));
            }

            await api.post("/auto-posting", form);

            toast.success(__("Successfully created auto posting.", "antimanual"));
        } catch (e: any) {
            toast.error(e?.message || __("Failed to create auto posting. Please try again.", "antimanual"));
        }

        setSubmitting(false);
    };

    return (
        <div className={css("grid grid-cols-2 gap-5 p-8")}>
            <Section className={css("col-span-1 bg-light flex flex-col gap-6")}>
                <h3>{__("Preferences", "antimanual")}</h3>
                <TextareaInput
                    value={topics}
                    onChange={setTopics}
                    label={__("Topics for the Post", "antimanual")}
                    help={__(
                        "Each time a post is generated, AI will randomly select one line from this list to use as the content topic.",
                        "antimanual"
                    )}
                    placeholder={__(
                        "WordPress security tips\nAdvanced SEO strategies\nResponsive web design techniques\nSocial media marketing campaigns\nContent creation with AI",
                        "antimanual"
                    )}
                    rows={5}
                />
                <div>
                    <FormBox label={__("Tone", "antimanual")} help={__("AI will write the post content in this tone.", "antimanual")} />
                    <Tone {...{ tone, setTone, modifiedTone, setModifiedTone }} />
                </div>
                <div className={css("grid grid-cols-2 gap-6")}>
                    <Select
                        value={language}
                        onChange={setLanguage}
                        label={__("Article Language", "antimanual")}
                        help={__("AI will write the post content in the selected language.", "antimanual")}
                        options={GPT_LANGUAGES}
                    />
                    <ArticleLength length={length} setLength={setLength} />
                    <Select
                        value={slugLang}
                        onChange={setSlugLang}
                        label={__("Slug Language", "antimanual")}
                        help={__("AI will generate a slug for the generated post in the selected language.", "antimanual")}
                        options={GPT_LANGUAGES}
                    />
                    <SlugMaxLength slugMaxLength={slugMaxLength} setSlugMaxLength={setSlugMaxLength} />
                    <Select
                        value={author}
                        onChange={setAuthor}
                        label={__("Post Author", "antimanual")}
                        help={__("Selected user will be the author of the generated posts.", "antimanual")}
                        options={users}
                    />
                    <Select
                        value={type}
                        onChange={setType}
                        label={__("Post Type", "antimanual")}
                        help={__("Select the post type of the generated posts.", "antimanual")}
                        options={POST_TYPES.map((type) => ({ value: type.id, label: type.label }))}
                    />
                    <Select
                        value={status}
                        onChange={setStatus}
                        label={__("Post Status", "antimanual")}
                        help={__("Select the status of the generated posts.", "antimanual")}
                        options={POST_STATUSES}
                    />
                </div>
            </Section>
            <div className={css("col-span-1")}>
                <KnowledgeSources handleFileChange={setFiles} />
                <Schedule {...{ expDate, setExpDate, schedule, setSchedule }} />
            </div>
            <div className={css("col-span-2")}>
                <div className={css("max-w-sm mx-auto")}>
                    <Button
                        variant="primary"
                        className={css("w-full py-3")}
                        disabled={submitting || postingsLoading}
                        loading={submitting}
                        onClick={() => {
                            const total = Number(postings?.length);
                            const limit = LIMITS.AUTO_POSTING;

                            if (total >= limit) {
                                showProAlert(
                                    __("Limit Exceeded", "antimanual") + ` (${total}/${limit})`,
                                    __("You have reached the limit of auto postings. Upgrade to create more auto postings.", "antimanual")
                                );
                                return;
                            }

                            handleSubmit();
                        }}
                    >
                        {__("Create Auto Posting", "antimanual")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
